import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { wines } from '../data/wines';
import { getVisibleWines, findWineBySlug, getClassificationColor, formatPrice } from '../utils/wineHelpers';
import WineGlassSmallIcon from './icons/WineGlassSmallIcon';
import UtensilsSmallIcon from './icons/UtensilsSmallIcon';
import FlaskIcon from './icons/FlaskIcon';
import ChatIcon from './icons/ChatIcon';
import VideoIcon from './icons/VideoIcon';
import CheckIcon from './icons/CheckIcon';
import XIcon from './icons/XIcon';

const WineOptions = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const wine = findWineBySlug(getVisibleWines(wines), slug);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [imageError, setImageError] = useState(false);

  if (!wine) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-wine-dark via-[#1e1a2e] to-wine-dark flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-white text-xl mb-4">Vinho não encontrado</p>
          <button onClick={() => navigate('/')} className="text-wine-gold hover:text-white transition-colors">
            Voltar ao início
          </button>
        </div>
      </div>
    );
  }

  const options = [
    { id: 'about', icon: <WineGlassSmallIcon />, title: 'Sobre este Vinho', feedback: null },
    { id: 'pairing', icon: <UtensilsSmallIcon />, title: 'Harmonizações Sugierizas', feedback: 'positive' },
    { id: 'technical', icon: <FlaskIcon />, title: 'Detalhes Técnicos', feedback: null },
  ];

  const handleOptionClick = (optionId) => {
    if (optionId === 'about') {
      setSelectedOptions((prev) => ({
        ...prev,
        about: prev.about === 'negative' ? null : 'negative',
      }));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-wine-dark via-[#1e1a2e] to-wine-dark flex items-center justify-center p-4">
      <div className="w-full max-w-md mx-auto">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <div className="w-40 h-64 bg-gradient-to-br from-[#3a3545] to-[#2a2535] rounded-2xl flex items-center justify-center shadow-xl overflow-hidden p-2">
              {imageError ? (
                <div className="w-16 h-48 bg-gradient-to-b from-[#4a2525] via-[#2a1515] to-[#1a0a0a] rounded-full" />
              ) : (
                <img
                  src={wine.image}
                  alt={wine.name}
                  className="h-full w-auto object-contain"
                  onError={() => setImageError(true)}
                />
              )}
            </div>
          </div>

          <h1 className="text-white text-3xl font-serif mb-2">{wine.name}</h1>
          <div className="flex items-center justify-center gap-2 mb-1">
            <span className={`px-2 py-1 rounded-full text-xs font-semibold uppercase ${getClassificationColor(wine.classification)}`}>
              {wine.classification}
            </span>
            <span className="text-gray-500">•</span>
            <span className="text-gray-400">{wine.grape}</span>
          </div>
          <p className="text-gray-400 text-lg">{formatPrice(wine.price)}</p>
        </div>

        <div className="space-y-4 mb-6">
          {options.map((option) => {
            const currentFeedback = selectedOptions[option.id] || option.feedback;
            const isNegative = currentFeedback === 'negative';
            const isPositive = currentFeedback === 'positive';

            return (
              <button
                key={option.id}
                type="button"
                onClick={() => handleOptionClick(option.id)}
                className={`w-full bg-gradient-to-br from-[#2a2535] to-[#1a1625] rounded-2xl p-5 flex items-center gap-4 transition-all duration-300 hover:scale-102 ${
                  isNegative ? 'ring-2 ring-red-500' : isPositive ? 'ring-2 ring-green-500' : ''
                }`}
              >
                <div className="flex-shrink-0 text-wine-gold">
                  {isNegative ? <XIcon /> : isPositive ? <CheckIcon /> : option.icon}
                </div>
                <div className="text-left flex-1">
                  <span className={`text-lg ${isNegative ? 'text-red-400' : isPositive ? 'text-green-400' : 'text-white'}`}>
                    {isNegative ? 'Pense duas vezes se...' : option.title}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        <div className="flex gap-4">
          <button type="button" className="flex-1 bg-transparent border-2 border-wine-gold text-wine-gold rounded-full py-4 px-6 flex items-center justify-center gap-2 hover:bg-wine-gold/10 transition-colors duration-300">
            <ChatIcon />
            <span className="font-medium">CHAT POR TEXTO</span>
          </button>
          <button type="button" className="flex-1 bg-wine-gold hover:bg-[#b89951] text-wine-dark font-semibold rounded-full py-4 px-6 flex items-center justify-center gap-2 transition-colors duration-300">
            <VideoIcon />
            <span>CHAMADA DE VIDEO</span>
          </button>
        </div>

        <button
          type="button"
          onClick={() => navigate(`/wine/${wine.slug}`)}
          className="w-full mt-6 text-gray-400 hover:text-white transition-colors"
        >
          ← Voltar
        </button>
      </div>
    </div>
  );
};

export default WineOptions;
